import React, { useCallback, useEffect, useState } from 'react';
import { isToday } from 'date-fns';
import { Cross1Icon } from '@radix-ui/react-icons';
import { AlertError, AlertSuccess, AlertWarning } from '@producer/ui-kit';

import { formatTimeOfDay } from '../../../core/services/date.service';
import { Robot } from '../../../components/icons/Robot';
import { Toast } from '../models/toast';

import styles from './toast-item.module.scss';
type ToastItemProps = {
  toast: Toast;
};

const formattingDayPeriodValues = () => {
  const time = formatTimeOfDay(new Date());

  return isToday(new Date()) ? `Today ${time}` : time;
};

const selectAlertType = (type: string) => {
  switch (type) {
    case 'success':
      return <AlertSuccess />;
    case 'error':
      return <AlertError />;
    case 'warning':
      return <AlertWarning />;
    case 'robot':
      return <Robot />;
  }
};

export const ToastItem: React.FC<ToastItemProps> = ({ toast }) => {
  const [time, setTime] = useState<any>(0);
  const currentMessageTime = formattingDayPeriodValues();
  const icon = selectAlertType(toast.type);

  const setToastTimeout = useCallback(() => {
    const deleteElement = setTimeout(() => toast.delete(), 3000);
    setTime(deleteElement);
  }, [toast]);

  useEffect(() => {
    setToastTimeout();
  }, [setToastTimeout]);

  return (
    <Element onMouseOver={() => clearTimeout(time)} onMouseLeave={setToastTimeout}>
      <CloseIcon onClick={() => toast.delete()}>
        <Cross1Icon />
      </CloseIcon>

      <AlertContent>
        <AlertIconType>{icon}</AlertIconType>

        <AlertInformation>
          <AlertText>{toast.message}</AlertText>
          <TimeBox>{toast.subheader || currentMessageTime}</TimeBox>
        </AlertInformation>
      </AlertContent>
    </Element>
  );
};

export const Element = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div className={className ? `${styles['element']} ${className}` : styles['element']} {...props} />
);

export const CloseIcon = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['close-icon']} ${className}` : styles['close-icon']}
    {...props}
  />
);

const AlertContent = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['alert-content']} ${className}` : styles['alert-content']}
    {...props}
  />
);

const AlertIconType = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={className ? `${styles['alert-icon-type']} ${className}` : styles['alert-icon-type']}
    {...props}
  />
);

const AlertText = ({ className, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
  <p
    className={className ? `${styles['alert-text']} ${className}` : styles['alert-text']}
    {...props}
  />
);

const AlertInformation = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className ? `${styles['alert-information']} ${className}` : styles['alert-information']
    }
    {...props}
  />
);

const TimeBox = ({ className, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
  <p className={className ? `${styles['time-box']} ${className}` : styles['time-box']} {...props} />
);
